import { FC, Fragment, memo, forwardRef } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Components
import { Button } from 'components/Buttons';
import MediaDialog from 'dialogs/Media.dialog';

type Props = {
  label?: string;
  value: string;
  onChange: (videoUrl:string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
};

const VideoUpload:FC<Props> = forwardRef<any, Props>(({
  // Props
  label = '', value, onChange, required = false, disabled = false,
  error = false, errorText = '', helperText = '', ...otherProps
}, ref) => {
  const { t } = useTranslation();

  const handleClickClear = () => {
    onChange('');
  }

  const { open, toggle } = useToggle();

  return (
    <Fragment>
      {open ? (
        <MediaDialog open={open} onClose={toggle} type="video" onChange={onChange} />
      ) : null}

      <div className="mb-4">
        <input ref={ref} type="hidden" name={(otherProps as any).name} />

        {label ? (
          <span className={`block text-white text-sm font-semibold mb-1 ${disabled ? 'opacity-50' : ''}`}>
            {t(label)}{required ? <Fragment>&nbsp;*</Fragment> : null}
          </span>
        ) : null}
        <div className="mb-2">
          {value ? (
            <div className="aspect-w-16 aspect-h-9">
              <video className="w-full h-full object-center object-cover" controls>
                <source src={value} type="video/mp4" />
              </video>
            </div>
          ) : (
            <span className="text-white text-xs font-light">{t('labels.chooseVideo')}</span>
          )}
        </div>
        <div className="mb-1">
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={toggle}
          >{t('buttons.browse')}</Button>
          <Button
            className="ml-2"
            variant="outlined"
            disabled={!value}
            onClick={handleClickClear}
          >{t('buttons.clear')}</Button>
        </div>
        {helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{t(helperText)}</span> : null}
        {error && errorText ? <span className="block text-red-500 text-sm py-1 px-4">{t(errorText)}</span> : null}
      </div>
    </Fragment>
  )
});

export default memo(VideoUpload, (prev, next) => {
  return prev.value === next.value && prev.error === next.error
});
